import { useEffect, useState } from 'react'
import { Plus, Play, Pause, Trash2, CheckCircle } from 'lucide-react'
import { api } from '../services/api'
import type { Campaign } from '../types'

export default function CampaignManager() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [loading, setLoading] = useState(true)

  const fetchCampaigns = async () => {
    try {
      setLoading(true)
      const data = await api.getCampaigns()
      setCampaigns(data)
    } catch (error) {
      console.error('Failed to fetch campaigns:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCampaigns()
  }, [])

  const handleStatusChange = async (campaignId: string, status: 'active' | 'paused' | 'completed') => {
    try {
      await api.updateCampaignStatus(campaignId, status)
      await fetchCampaigns()
    } catch (error) {
      console.error('Failed to update campaign:', error)
    }
  }

  const handleDelete = async (campaignId: string) => {
    if (!confirm('정말로 이 캠페인을 삭제하시겠습니까?')) return
    
    try {
      await api.deleteCampaign(campaignId)
      await fetchCampaigns()
    } catch (error) {
      console.error('Failed to delete campaign:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">캠페인 관리</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          새 캠페인
        </button>
      </div>

      {/* Campaign Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {campaigns.map((campaign) => (
          <CampaignCard
            key={campaign.id}
            campaign={campaign}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {campaigns.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400 text-lg">캠페인이 없습니다</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 text-sky-500 hover:text-sky-400"
          >
            첫 캠페인 만들기
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateCampaignModal
          onClose={() => setShowCreate(false)}
          onSuccess={fetchCampaigns}
        />
      )}
    </div>
  )
}

function CampaignCard({
  campaign,
  onStatusChange,
  onDelete,
}: {
  campaign: Campaign
  onStatusChange: (id: string, status: 'active' | 'paused' | 'completed') => void
  onDelete: (id: string) => void
}) {
  const statusColors = {
    draft: 'bg-gray-500',
    active: 'bg-green-500',
    paused: 'bg-yellow-500',
    completed: 'bg-blue-500',
  }

  const statusText = {
    draft: '초안',
    active: '진행중',
    paused: '일시정지',
    completed: '완료',
  }

  return (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white">{campaign.name}</h3>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm text-white ${statusColors[campaign.status]}`}>
            {statusText[campaign.status]}
          </span>
        </div>
        <div className="flex space-x-2">
          {campaign.status === 'active' && (
            <button
              onClick={() => onStatusChange(campaign.id, 'paused')}
              className="p-2 text-yellow-500 hover:bg-slate-700 rounded"
            >
              <Pause className="h-5 w-5" />
            </button>
          )}
          {campaign.status === 'paused' && (
            <button
              onClick={() => onStatusChange(campaign.id, 'active')}
              className="p-2 text-green-500 hover:bg-slate-700 rounded"
            >
              <Play className="h-5 w-5" />
            </button>
          )}
          {campaign.status !== 'completed' && (
            <button
              onClick={() => onStatusChange(campaign.id, 'completed')}
              className="p-2 text-blue-500 hover:bg-slate-700 rounded"
            >
              <CheckCircle className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={() => onDelete(campaign.id)}
            className="p-2 text-red-500 hover:bg-slate-700 rounded"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between text-gray-400">
          <span>플랫폼:</span>
          <span className="text-white">{campaign.platforms.join(', ')}</span>
        </div>
        <div className="flex justify-between text-gray-400">
          <span>채널:</span>
          <span className="text-white">{campaign.channels.join(', ')}</span>
        </div>
        <div className="flex justify-between text-gray-400">
          <span>예산:</span>
          <span className="text-white">${campaign.budget.toLocaleString()}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-700">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-gray-400 text-xs">리드</p>
            <p className="text-white font-semibold">{campaign.performance.leads.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs">전환</p>
            <p className="text-white font-semibold">{campaign.performance.conversions.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs">매출</p>
            <p className="text-green-400 font-semibold">${campaign.performance.revenue.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs">ROI</p>
            <p className="text-sky-400 font-semibold">{campaign.roi.toFixed(1)}%</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function CreateCampaignModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void
  onSuccess: () => void
}) {
  const [formData, setFormData] = useState({
    name: '',
    platforms: [] as string[],
    channels: [] as string[],
    duration_days: 30,
    budget: 10000,
  })

  const availablePlatforms = ['goldsaju', 'monaxio', 'hahaostory', 'youtubegenius', 'crowdsui']
  const availableChannels = ['instagram', 'whatsapp', 'email', 'youtube', 'telegram', 'linkedin', 'tiktok']

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.createCampaign(formData)
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to create campaign:', error)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-white mb-4">새 캠페인 만들기</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-2">캠페인 이름</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-gray-400 mb-2">플랫폼 선택</label>
            <div className="space-y-2">
              {availablePlatforms.map((platform) => (
                <label key={platform} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.platforms.includes(platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          platforms: [...formData.platforms, platform],
                        })
                      } else {
                        setFormData({
                          ...formData,
                          platforms: formData.platforms.filter((p) => p !== platform),
                        })
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-white">{platform}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">채널 선택</label>
            <div className="space-y-2">
              {availableChannels.map((channel) => (
                <label key={channel} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.channels.includes(channel)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          channels: [...formData.channels, channel],
                        })
                      } else {
                        setFormData({
                          ...formData,
                          channels: formData.channels.filter((c) => c !== channel),
                        })
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-white">{channel}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">기간 (일)</label>
            <input
              type="number"
              value={formData.duration_days}
              onChange={(e) => setFormData({ ...formData, duration_days: Number(e.target.value) })}
              className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg"
              min={1}
              required
            />
          </div>

          <div>
            <label className="block text-gray-400 mb-2">예산 ($)</label>
            <input
              type="number"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: Number(e.target.value) })}
              className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg"
              min={100}
              required
            />
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              className="flex-1 bg-sky-600 text-white py-2 rounded-lg hover:bg-sky-700"
            >
              생성하기
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-700 text-white py-2 rounded-lg hover:bg-slate-600"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
